/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import {
	Tenant,
	ITenantMetadata,
	TenantErrors,
	FancyDatabase,
	IRefLike,
	ISnapshotLike,
} from '@touchplan/common';
import {
	fancyFirebaseGetters,
	fancyFirebaseReferencer,
	DEFAULT_TENANT_ID,
} from '@touchplan/fancy-referencer';
import { logger } from '@touchplan/logging';

/** a tuple that covers the firebase key, and the tenant specific metadata from firebase */
type PseudoSnapshot = [string, ITenantMetadata];

/**
 * Provides various utility firebase fetch methods for [[Tenant]]'s.
 * Including some lazy-load fetches that rely on the internal caching of a Tenant
 * Everything kept very "promise-y" for maximum portability
 */
export class TenantGetterService {
	private _getFirstChild<T>(snapshot: ISnapshotLike<T>): ISnapshotLike<ITenantMetadata> {
		if (snapshot.numChildren() < 1) {
			throw new Error('no children');
		}

		let result!: ISnapshotLike<ITenantMetadata>;
		snapshot.forEach<ITenantMetadata>((s) => {
			result = s;
			return true;
		});
		return result;
	}

	/**
	 * grab raw tenant data and id. Mostly prefer [[getTenantViaAuthId]]
	 */
	public fetchTenantDataViaAuthId<T extends IRefLike>(
		fancyDatabase: FancyDatabase<T>,
		authTenantId?: string
	): Promise<PseudoSnapshot | undefined> {
		if (!authTenantId || authTenantId === DEFAULT_TENANT_ID) {
			return Promise.resolve(undefined);
		}

		const tenantParentPath = fancyFirebaseGetters.getTenantPath();
		const tenantParentRef = fancyFirebaseReferencer.getRef(tenantParentPath);
		const fancyPromise = fancyDatabase.getIndexedChildValue<ITenantMetadata>(
			tenantParentRef,
			'firebaseTenantId',
			authTenantId
		);

		return fancyPromise.then((snapshot) => {
			if (!snapshot.exists() || snapshot.numChildren() !== 1) {
				throw new Error(`data missing for authTenantId (firebaseTenantId): ${authTenantId}`);
			}

			const childSnapshot = this._getFirstChild(snapshot);
			return [childSnapshot.key!, childSnapshot.val()!];
		});
	}

	/**
	 * grab raw tenant data and id. Mostly prefer [[getTenantViaRtdbId]]
	 */
	public fetchTenantDataViaRtdbId<T extends IRefLike>(
		fancyDatabase: FancyDatabase<T>,
		rtdbTenantId?: string
	): Promise<PseudoSnapshot | undefined> {
		if (!rtdbTenantId || rtdbTenantId === DEFAULT_TENANT_ID) {
			return Promise.resolve(undefined);
		}

		const tenantPath = fancyFirebaseGetters.getTenantPath(rtdbTenantId);
		const tenantRef = fancyFirebaseReferencer.getRef(tenantPath);

		return fancyDatabase.get<ITenantMetadata>(tenantRef).then((tenantMeta) => {
			if (!tenantMeta.exists()) {
				throw new Error(`data missing for rtdbTenantId: ${rtdbTenantId}`);
			}
			return [tenantMeta.key!, tenantMeta.val()!];
		});
	}

	public async createTenant(
		pseudoSnapshotPromise: Promise<PseudoSnapshot | undefined>
	): Promise<Tenant | undefined> {
		const pseudoSnapshot = await pseudoSnapshotPromise;
		return this.createTenantSync(pseudoSnapshot);
	}

	public createTenantSync(pseudoSnapshot: PseudoSnapshot | undefined): Tenant | undefined {
		if (!pseudoSnapshot) {
			return undefined;
		} //touchplan tenant, skip for now

		const metadata = pseudoSnapshot[1];
		const authTenantId: string = metadata.firebaseTenantId;
		const rtdbTenantId: string = pseudoSnapshot[0];

		return Tenant.create(authTenantId, rtdbTenantId, metadata);
	}

	private _tryGetTenant(
		tenantId: string | undefined,
		tenantGetter: (tenantId: string) => Tenant
	): Tenant | undefined {
		let tenant: Tenant | undefined;
		try {
			if (tenantId) {
				tenant = tenantGetter(tenantId);
			}
		} catch (err) {
			//this error is expected and ignorable, everything else is not
			if (err.message !== TenantErrors.NotCached) {
				logger.error('_tryGetTenant error', err);
				throw err;
			} else {
				logger.warn('_tryGetTenant expected - NotCached error', err);
			}
		}

		return tenant;
	}

	private _checkForDefaultTenant(tenantId?: string): Tenant | undefined {
		if (!tenantId || tenantId === DEFAULT_TENANT_ID) {
			return Tenant.getDefaultTenant();
		}
		return undefined;
	}

	/**
	 * Get the tenant using the auth based tenantId. Prefers cached version if available
	 */
	public getTenantViaAuthId<T extends IRefLike>(
		fancyDatabase: FancyDatabase<T>,
		authTenantId?: string
	): Promise<Tenant | undefined> {
		//is it the default?
		let tenant = this._checkForDefaultTenant(authTenantId);
		if (!tenant) {
			//or already initialized?
			tenant = this._tryGetTenant(authTenantId, (id) => Tenant.getByAuthTenantId(id));
		}

		//guess not, fetch a new one
		if (!tenant) {
			return this.createTenant(this.fetchTenantDataViaAuthId(fancyDatabase, authTenantId));
		}

		return Promise.resolve(tenant);
	}

	/**
	 * Get the tenant using the rtdb based tenantId. Prefers cached version if available
	 */
	public getTenantViaRtdbId<T extends IRefLike>(
		fancyDatabase: FancyDatabase<T>,
		rtdbTenantId: string
	): Promise<Tenant | undefined> {
		//is it the default?
		let tenant = this._checkForDefaultTenant(rtdbTenantId);
		if (!tenant) {
			//or already initialized?
			tenant = this._tryGetTenant(rtdbTenantId, (id) => Tenant.getByRtdbTenantId(id));
		}

		//guess not, fetch a new one
		if (!tenant) {
			return this.createTenant(this.fetchTenantDataViaRtdbId(fancyDatabase, rtdbTenantId));
		}

		return Promise.resolve(tenant);
	}
}

export const tenantGetterService: TenantGetterService = new TenantGetterService();
